import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Phone, Loader2, Save, RefreshCcw, Trash2, Sparkles, Info, Link2,
  CheckCircle2, XCircle, Mic, Bot, MessageSquare, Volume2, FileDown,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { ScrollArea } from "../../components/ui/scroll-area";
import { toast } from "sonner";
import AdminVoiceAdvisors from "./AdminVoiceAdvisors";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtBerlin = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 16).replace("T", " ");
    return d.toLocaleString("de-DE", {
      timeZone: "Europe/Berlin",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16).replace("T", " ");
  }
};

const PROJECT_OPTIONS = [
  { value: "hypefactory", label: "HypeFactory (Lea) – Promotion & Events" },
  { value: "factoryhype", label: "FactoryHype (Leonie) – NGO Fundraising" },
  { value: "factoryhype_v2", label: "FactoryHype V2 (Leonie – Verschärft)" },
  { value: "hypeconnect", label: "HypeConnect (Lisa) – B2B Beratung" },
];

const MODEL_OPTIONS = [
  { value: "gpt-4o", label: "GPT-4o (empfohlen, ~1,5s)" },
  { value: "gpt-4o-mini", label: "GPT-4o-mini (schnellster, ~1s)" },
  { value: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5 (klügste, ~5s)" },
];

const VOICE_OPTIONS = [
  { value: "Polly.Vicki-Neural", label: "Vicki-Neural (Frau, natürlich)" },
  { value: "Polly.Daniel-Neural", label: "Daniel-Neural (Mann, natürlich)" },
  { value: "Polly.Marlene", label: "Marlene (Frau, Standard)" },
  { value: "Polly.Hans", label: "Hans (Mann, Standard)" },
];

const TIMEOUT_OPTIONS = [
  { value: "0.5", label: "0,5 Sek (blitzschnell)" },
  { value: "1", label: "1 Sek (schnell)" },
  { value: "2", label: "2 Sek (entspannt)" },
  { value: "auto", label: "Auto (Twilio entscheidet)" },
];

export default function AdminVoiceBot({ authToken }) {
  const [tab, setTab] = useState("settings");

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-5xl mx-auto space-y-6"
      data-testid="voice-bot-admin-page"
    >
      <header className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
          <Phone className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Voice-Bot (Telefon)</h1>
          <p className="text-sm text-gray-500">
            Konfiguriere Nummern, Begrüßung, Chat, Modell &amp; Stimme. Verwalte Anrufe und Transkripte.
          </p>
        </div>
      </header>

      <div className="flex gap-2 border-b border-gray-200">
        {[
          { id: "settings", label: "Einstellungen", icon: Bot },
          { id: "calls", label: "Anrufe & Transkripte", icon: MessageSquare },
          { id: "advisors", label: "Beraterinnen & SMS", icon: Phone },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 -mb-px border-b-2 text-sm font-medium transition-colors flex items-center gap-2 ${
              tab === t.id
                ? "border-emerald-500 text-emerald-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            data-testid={`voice-bot-tab-${t.id}`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "settings" ? (
        <SettingsPanel authToken={authToken} />
      ) : tab === "calls" ? (
        <CallsPanel authToken={authToken} />
      ) : (
        <AdminVoiceAdvisors authToken={authToken} />
      )}
    </motion.div>
  );
}

/* ============================================================ SETTINGS */

function SettingsPanel({ authToken }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [s, setS] = useState(null);
  const [status, setStatus] = useState(null);
  const [wiringSid, setWiringSid] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/voice-bot/settings`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/voice-bot/twilio-status`, { headers: { Authorization: `Bearer ${authToken}` } }),
      ]);
      setS(a.data);
      setStatus(b.data);
    } catch (e) {
      toast.error("Einstellungen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/voice-bot/settings`, s, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setS(r.data);
      toast.success("Einstellungen gespeichert. Ab jetzt aktiv für neue Anrufe.");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const wire = async (numberSid) => {
    setWiringSid(numberSid);
    try {
      await axios.post(
        `${API}/admin/voice-bot/attach-number`,
        { number_sid: numberSid },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success("Nummer verkabelt – Anrufer erreichen jetzt unseren Bot.");
      load();
    } catch (e) {
      toast.error("Verkabelung fehlgeschlagen: " + (e?.response?.data?.detail || e.message));
    } finally {
      setWiringSid(null);
    }
  };

  if (loading || !s) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-6">
      {/* Twilio Live Status */}
      <Card title="Twilio-Konto (live)" icon={<Info className="h-5 w-5 text-blue-500" />} testId="twilio-status-card">
        {status?.configured ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Kv label="Konto" value={status.friendly_name} />
            <Kv label="Status" value={status.status} pill={status.status === "active" ? "green" : "red"} />
            <Kv label="Typ" value={status.type} />
            <Kv label="Guthaben" value={`${parseFloat(status.balance || 0).toFixed(2)} ${status.currency}`} />
          </div>
        ) : (
          <div className="text-sm text-red-600" data-testid="twilio-error">
            {status?.error || "Nicht konfiguriert"}
          </div>
        )}

        {status?.numbers?.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-xs font-semibold uppercase text-gray-500">Nummern im Konto</div>
            {status.numbers.map((n) => {
              const attached =
                (n.voice_url || "").includes("/api/twilio/voice/incoming");
              return (
                <div
                  key={n.sid}
                  className="flex items-center justify-between rounded-lg border border-gray-200 p-3 bg-white text-sm"
                  data-testid={`twilio-number-${n.sid}`}
                >
                  <div>
                    <div className="font-mono font-semibold">{n.phone_number}</div>
                    <div className="text-xs text-gray-500 truncate max-w-xl">
                      Voice-URL: {n.voice_url || "(nicht gesetzt)"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {attached ? (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Verkabelt
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        <XCircle className="h-3 w-3 mr-1" /> Nicht verkabelt
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => wire(n.sid)}
                      disabled={wiringSid === n.sid}
                      data-testid={`wire-btn-${n.sid}`}
                    >
                      {wiringSid === n.sid ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <Link2 className="h-3 w-3 mr-1" />
                          {attached ? "Neu verkabeln" : "Verkabeln"}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={load} data-testid="reload-status-btn">
            <RefreshCcw className="h-3 w-3 mr-2" /> Aktualisieren
          </Button>
        </div>
      </Card>

      {/* Telefonnummern */}
      <Card title="Telefonnummern" icon={<Phone className="h-5 w-5 text-emerald-500" />}>
        <div className="grid md:grid-cols-2 gap-4">
          <NumberField
            label="Handynummer"
            emoji="📱"
            number={s.mobile_number}
            enabled={s.mobile_enabled}
            onNumber={(v) => set("mobile_number", v)}
            onEnabled={(v) => set("mobile_enabled", v)}
            testId="mobile"
          />
          <NumberField
            label="Festnetznummer"
            emoji="☎️"
            number={s.landline_number}
            enabled={s.landline_enabled}
            onNumber={(v) => set("landline_number", v)}
            onEnabled={(v) => set("landline_enabled", v)}
            testId="landline"
          />
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Nummern werden hier nur zur Anzeige/Referenz gespeichert. Die tatsächliche Verkabelung
          zwischen Twilio und Bot passiert oben mit dem <em>&bdquo;Verkabeln&ldquo;</em>-Button.
        </p>
      </Card>

      {/* Begrüßung */}
      <Card title="Begrüßung" icon={<Volume2 className="h-5 w-5 text-purple-500" />}>
        <Textarea
          value={s.greeting_message}
          onChange={(e) => set("greeting_message", e.target.value)}
          rows={3}
          className="text-sm"
          data-testid="greeting-input"
        />
        <p className="text-xs text-gray-500 mt-2">
          Erste Nachricht die der Anrufer hört. Kurz, warm und mit einer offenen Frage am Ende.
        </p>
      </Card>

      {/* Verhalten */}
      <Card title="Verhalten & Modell" icon={<Bot className="h-5 w-5 text-indigo-500" />}>
        <div className="grid md:grid-cols-2 gap-5">
          <Select
            label="Chat / Persona"
            value={s.project_id}
            onChange={(v) => set("project_id", v)}
            options={PROJECT_OPTIONS}
            testId="project-select"
          />
          <Select
            label="KI-Modell"
            value={s.model}
            onChange={(v) => set("model", v)}
            options={MODEL_OPTIONS}
            testId="model-select"
          />
          <Select
            label="Reaktionszeit (speechTimeout)"
            value={s.speech_timeout}
            onChange={(v) => set("speech_timeout", v)}
            options={TIMEOUT_OPTIONS}
            testId="timeout-select"
          />
          <div className="flex flex-col justify-end">
            <label className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
              Unterbrechbar (Barge-In)
            </label>
            <div className="flex items-center gap-3 h-11">
              <Switch
                checked={s.barge_in_enabled}
                onCheckedChange={(v) => set("barge_in_enabled", v)}
                data-testid="bargein-toggle"
              />
              <span className="text-sm text-gray-700">
                {s.barge_in_enabled ? "Anrufer kann den Bot mitten im Satz unterbrechen" : "Bot spricht immer aus"}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Stimme */}
      <Card title="Stimme" icon={<Mic className="h-5 w-5 text-pink-500" />}>
        <Select
          label="TTS-Stimme"
          value={s.tts_voice}
          onChange={(v) => set("tts_voice", v)}
          options={VOICE_OPTIONS}
          testId="voice-select"
        />
        <p className="text-xs text-gray-500 mt-3">
          Alle Stimmen sind Amazon Polly (Twilio Standard). Kostenlose Neural-Stimmen klingen deutlich natürlicher.
        </p>
      </Card>

      {/* Aufnahme */}
      <Card title="Anruf-Aufzeichnung" icon={<Mic className="h-5 w-5 text-red-500" />} testId="recording-card">
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 mb-4 text-xs text-amber-900">
          <strong>⚠️ Rechtlicher Hinweis:</strong> In Deutschland (§201 StGB) ist das Aufzeichnen
          eines Telefongesprächs <em>ohne Einwilligung beider Parteien</em> strafbar. Bei aktiver
          Aufnahme wird der Anrufer <strong>automatisch VOR der Begrüßung</strong> darüber
          informiert. Die rechtliche Verantwortung für die Nutzung liegt bei dir.
        </div>
        <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
          <div>
            <div className="font-medium text-gray-900">Anrufe aufzeichnen</div>
            <div className="text-xs text-gray-500 mt-1">
              Dual-Channel MP3 &mdash; anhörbar im Anruf-Detail. Standardmäßig AUS.
            </div>
          </div>
          <Switch
            checked={s.recording_enabled || false}
            onCheckedChange={(v) => set("recording_enabled", v)}
            data-testid="recording-toggle"
          />
        </div>
        {s.recording_enabled && (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
            ✓ Kurzer Rechtshinweis wird der Begrüßung automatisch vorangestellt (~3 Sek):
            <div className="italic mt-1 font-mono text-[11px]">
              &bdquo;Zur Qualitätssicherung nehmen wir das Gespräch auf.&ldquo;
            </div>
          </div>
        )}
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg"
          data-testid="voice-bot-save-btn"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Speichern…
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" /> Einstellungen speichern
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/* ============================================================ CALLS */

function CallsPanel({ authToken }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSid, setSelectedSid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [analysis, setAnalysis] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingSingle, setAnalyzingSingle] = useState(false);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, inbound: 0, outbound: 0 });
  const [directionFilter, setDirectionFilter] = useState("all"); // 'all' | 'inbound' | 'outbound'
  const [folders, setFolders] = useState([]);
  const [folderFilter, setFolderFilter] = useState("all"); // 'all' | 'unassigned' | folder.id
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [autoSortEnabled, setAutoSortEnabled] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 200;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadFolders = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/voice-folders`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setFolders(r.data.folders || []);
      setUnassignedCount(r.data.unassigned || 0);
      setAutoSortEnabled(!!r.data.settings?.auto_sort_enabled);
    } catch {
      /* silent */
    }
  }, [authToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page_size: String(PAGE_SIZE), page: String(page) });
      if (directionFilter !== "all") params.set("direction", directionFilter);
      if (folderFilter !== "all") params.set("folder_id", folderFilter);
      const r = await axios.get(`${API}/admin/phone-calls?${params.toString()}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setCalls(r.data.calls || []);
      setTotal(r.data.total || 0);
      if (r.data.counts) setCounts(r.data.counts);
    } catch {
      toast.error("Anrufe konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
    loadFolders();
  }, [authToken, directionFilter, folderFilter, loadFolders, page]);

  const syncAndReload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/elevenlabs/sync`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const inSynced = r.data?.inbound?.synced ?? 0;
      const outSynced = r.data?.outbound?.synced ?? 0;
      if (inSynced + outSynced > 0) {
        toast.success(`${inSynced + outSynced} neue Anrufe von ElevenLabs synchronisiert`);
      } else {
        toast.info("Keine neuen Anrufe – alles aktuell");
      }
    } catch {
      toast.error("Sync mit ElevenLabs fehlgeschlagen");
    }
    await load();
  }, [authToken, load]);

  const moveCallsToFolder = async (callSids, folderId) => {
    try {
      const r = await axios.post(
        `${API}/admin/phone-calls/move-folder`,
        { call_sids: callSids, folder_id: folderId },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success(`${r.data.moved} Anruf(e) verschoben`);
      setChecked(new Set());
      await load();
    } catch {
      toast.error("Verschieben fehlgeschlagen");
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(
        `${API}/admin/voice-folders`,
        { name: newFolderName.trim(), color: "sky" },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success(`Ordner "${newFolderName}" erstellt`);
      setNewFolderName("");
      setShowNewFolder(false);
      await loadFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erstellen fehlgeschlagen");
    }
  };

  const deleteFolder = async (folderId, folderName) => {
    if (!window.confirm(`Ordner "${folderName}" wirklich löschen? Anrufe werden auf "Alle" zurückgestuft.`)) return;
    try {
      await axios.delete(`${API}/admin/voice-folders/${folderId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success("Ordner gelöscht");
      if (folderFilter === folderId) setFolderFilter("all");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const toggleAutoSort = async () => {
    try {
      await axios.post(
        `${API}/admin/voice-folders/settings`,
        { enabled: !autoSortEnabled },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setAutoSortEnabled(!autoSortEnabled);
      toast.success(`Auto-Sortierung ${!autoSortEnabled ? "aktiviert" : "deaktiviert"}`);
    } catch {
      toast.error("Konnte Auto-Sortierung nicht ändern");
    }
  };

  const runAutoSortNow = async () => {
    try {
      const r = await axios.post(
        `${API}/admin/voice-folders/auto-sort-run`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success(`${r.data.moved} von ${r.data.checked} Anrufen einsortiert`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-Sortierung fehlgeschlagen");
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // Auto-Refresh wurde deaktiviert: dauerhaft störend beim Scrollen.
  // Nutze stattdessen den "Aktualisieren"-Button in der Pagination-Leiste.

  // Reset to page 1 when the filter changes
  useEffect(() => {
    setPage(1);
  }, [directionFilter, folderFilter]);

  const loadDetail = async (sid) => {
    setSelectedSid(sid);
    setDetail(null);
    try {
      const r = await axios.get(`${API}/admin/phone-calls/${sid}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setDetail(r.data);
    } catch {
      toast.error("Anruf konnte nicht geladen werden");
    }
  };

  const toggle = (sid) => {
    const next = new Set(checked);
    if (next.has(sid)) next.delete(sid);
    else next.add(sid);
    setChecked(next);
  };

  const bulkDelete = async (mode) => {
    if (!window.confirm(`Willst du wirklich "${mode}" Anrufe löschen?`)) return;
    try {
      const body = mode === "selected"
        ? { mode: "ids", call_sids: Array.from(checked) }
        : { mode };
      const r = await axios.post(`${API}/admin/phone-calls/bulk-delete`, body, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success(`${r.data.deleted} Anrufe gelöscht.`);
      setChecked(new Set());
      setSelectedSid(null);
      setDetail(null);
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const selectLastN = (n) => {
    // n = number or 'all'
    const take = n === "all" ? calls.length : n;
    const sids = calls.slice(0, take).map((c) => c.call_sid);
    setChecked(new Set(sids));
    toast.info(`${sids.length} Anrufe markiert – wähle jetzt „Verschieben" oder „Löschen"`);
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (folderFilter && folderFilter !== "all") params.set("folder_id", folderFilter);
      if (directionFilter && directionFilter !== "all") params.set("direction", directionFilter);
      const url = `${API}/admin/phone-calls/export/excel?${params.toString()}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${authToken}` },
        responseType: "blob",
      });
      const folder = folders.find((f) => f.id === folderFilter);
      const folderName = folderFilter === "all" ? "Alle"
                       : folderFilter === "unassigned" ? "Nicht-einsortiert"
                       : (folder?.name || folderFilter);
      const safeName = String(folderName).replace(/[^\w\-]+/g, "-");
      const filename = `anrufe-${safeName}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(response.data);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Excel exportiert: ${filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export fehlgeschlagen");
    }
  };


  const analyze = async () => {
    let sids = Array.from(checked);
    if (sids.length === 0) sids = calls.slice(0, 10).map((c) => c.call_sid);
    if (sids.length === 0) return toast.error("Keine Anrufe zum Analysieren");
    setAnalyzing(true);
    setAnalysis("");
    try {
      const r = await axios.post(
        `${API}/admin/phone-calls/analyze`,
        { call_sids: sids },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setAnalysis(r.data.analysis);
      toast.success(`${r.data.analyzed_count} Anrufe analysiert.`);
    } catch (e) {
      toast.error("Analyse fehlgeschlagen: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAnalyzing(false);
    }
  };

  const analyzeSingle = async () => {
    if (!detail?.call_sid) return;
    setAnalyzingSingle(true);
    try {
      const r = await axios.post(
        `${API}/admin/phone-calls/${detail.call_sid}/analyze-single`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setDetail((d) => ({ ...d, analysis: r.data.analysis }));
      toast.success("Anruf analysiert.");
    } catch (e) {
      toast.error("Analyse fehlgeschlagen: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAnalyzingSingle(false);
    }
  };

  return (
    <div className="grid md:grid-cols-[380px_1fr] gap-4">
      {/* LEFT: list */}
      <div className="rounded-2xl border border-gray-200 bg-white p-3 flex flex-col" data-testid="calls-list-col">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <span>Anrufe ({total})</span>
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                selectedSid || (checked && checked.size > 0)
                  ? "bg-gray-100 text-gray-500"
                  : "bg-emerald-50 text-emerald-700"
              }`}
              title={
                selectedSid
                  ? "Auto-Refresh pausiert (Anruf-Detail offen)"
                  : checked && checked.size > 0
                  ? "Auto-Refresh pausiert (Auswahl aktiv)"
                  : "Live-Modus: aktualisiert automatisch alle 30s"
              }
              data-testid="auto-refresh-indicator"
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  selectedSid || (checked && checked.size > 0)
                    ? "bg-gray-400"
                    : "bg-emerald-500 animate-pulse"
                }`}
              />
              {selectedSid || (checked && checked.size > 0) ? "Pausiert" : "Live"}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={exportExcel}
              disabled={loading || total === 0}
              data-testid="export-calls-excel-btn"
              title="Aktuelle Anruf-Liste als Excel exportieren (Name + Telefonnummer, gleiches Format wie Leads-Export)"
              className="text-xs h-8"
            >
              <FileDown className="h-3 w-3 mr-1" />
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={syncAndReload}
              disabled={loading}
              data-testid="reload-calls-btn"
              className="text-xs h-8"
            >
              <RefreshCcw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Sync…" : "Aktualisieren"}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mb-2" data-testid="direction-filter-tabs">
          {[
            { key: "all", label: "Alle", count: counts.all, icon: "📞" },
            { key: "inbound", label: "Eingehend", count: counts.inbound, icon: "⬅️" },
            { key: "outbound", label: "Ausgehend", count: counts.outbound, icon: "➡️" },
          ].map((t) => (
            <Button
              key={t.key}
              size="sm"
              variant={directionFilter === t.key ? "default" : "outline"}
              className={`text-xs px-2 py-1 h-7 ${
                directionFilter === t.key
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : ""
              }`}
              onClick={() => setDirectionFilter(t.key)}
              data-testid={`filter-${t.key}`}
            >
              {t.icon} {t.label} ({t.count})
            </Button>
          ))}
        </div>

        <div className="border border-gray-200 rounded-lg p-2 mb-3 bg-gray-50" data-testid="folder-panel">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] font-semibold uppercase text-gray-500 flex items-center gap-1">
              📁 Ordner
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-[10px] text-gray-600 cursor-pointer" title="Aktivierte Ordner sortieren neue Anrufe automatisch anhand der KI-Analyse ein">
                <Switch
                  checked={autoSortEnabled}
                  onCheckedChange={toggleAutoSort}
                  data-testid="auto-sort-switch"
                  className="scale-75"
                />
                <span>Auto</span>
              </label>
              {autoSortEnabled && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-[10px] h-6 px-1.5"
                  onClick={runAutoSortNow}
                  data-testid="run-autosort-btn"
                  title="Bestehende unsortierte Anrufe einsortieren"
                >
                  Jetzt sortieren
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-1">
            <Button
              size="sm"
              variant={folderFilter === "all" ? "default" : "outline"}
              className={`text-xs px-2 py-1 h-7 ${folderFilter === "all" ? "bg-gray-700 text-white" : ""}`}
              onClick={() => setFolderFilter("all")}
              data-testid="folder-all"
            >
              Alle ({counts.all})
            </Button>
            <Button
              size="sm"
              variant={folderFilter === "unassigned" ? "default" : "outline"}
              className={`text-xs px-2 py-1 h-7 ${folderFilter === "unassigned" ? "bg-gray-700 text-white" : ""}`}
              onClick={() => setFolderFilter("unassigned")}
              data-testid="folder-unassigned"
            >
              📥 Posteingang ({unassignedCount})
            </Button>
            {folders.map((f) => {
              const active = folderFilter === f.id;
              const colorClasses = {
                emerald: "bg-emerald-500 text-white border-emerald-600",
                red: "bg-red-500 text-white border-red-600",
                amber: "bg-amber-500 text-white border-amber-600",
                violet: "bg-violet-500 text-white border-violet-600",
                gray: "bg-gray-500 text-white border-gray-600",
                sky: "bg-sky-500 text-white border-sky-600",
                orange: "bg-orange-500 text-white border-orange-600",
              }[f.color] || "bg-gray-500 text-white border-gray-600";
              return (
                <div key={f.id} className="relative group">
                  <Button
                    size="sm"
                    variant="outline"
                    className={`text-xs px-2 py-1 h-7 pr-6 ${active ? colorClasses : ""}`}
                    onClick={() => setFolderFilter(f.id)}
                    data-testid={`folder-${f.id}`}
                  >
                    {f.name} ({f.count || 0})
                    {f.auto_criteria_enabled && (
                      <span className="ml-1 text-[9px] opacity-75" title="Auto-Sortierung aktiv">🤖</span>
                    )}
                  </Button>
                  {!f.is_system && (
                    <button
                      className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition text-[10px] text-red-500 hover:text-red-700 px-0.5"
                      onClick={(e) => { e.stopPropagation(); deleteFolder(f.id, f.name); }}
                      data-testid={`del-folder-${f.id}`}
                      title="Ordner löschen"
                    >
                      ✕
                    </button>
                  )}
                </div>
              );
            })}
            {showNewFolder ? (
              <div className="flex items-center gap-1">
                <Input
                  autoFocus
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") createFolder();
                    if (e.key === "Escape") { setShowNewFolder(false); setNewFolderName(""); }
                  }}
                  placeholder="Ordnername"
                  className="text-xs h-7 w-32 px-2"
                  data-testid="new-folder-input"
                />
                <Button size="sm" className="text-xs h-7 px-2 bg-emerald-600 text-white" onClick={createFolder} data-testid="save-folder-btn">
                  ✓
                </Button>
                <Button size="sm" variant="ghost" className="text-xs h-7 px-2" onClick={() => { setShowNewFolder(false); setNewFolderName(""); }}>
                  ✕
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="text-xs px-2 py-1 h-7 border-dashed"
                onClick={() => setShowNewFolder(true)}
                data-testid="new-folder-btn"
              >
                + Ordner
              </Button>
            )}
          </div>

          {checked.size > 0 && (
            <div className="mt-3 pt-3 border-t-2 border-emerald-300 bg-emerald-50 -mx-2 -mb-2 px-3 py-2.5 rounded-b-lg">
              <div className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1.5">
                <span className="bg-emerald-600 text-white rounded-full px-2 py-0.5 text-[10px]">
                  {checked.size}
                </span>
                <span>ausgewählte Anrufe verschieben in:</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button
                  size="sm"
                  className="text-xs h-7 px-2.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"
                  onClick={() => moveCallsToFolder([...checked], null)}
                  data-testid="move-to-none"
                >
                  📥 Posteingang
                </Button>
                {folders.map((f) => {
                  const bgMap = {
                    emerald: "bg-emerald-500 hover:bg-emerald-600",
                    red: "bg-red-500 hover:bg-red-600",
                    amber: "bg-amber-500 hover:bg-amber-600",
                    violet: "bg-violet-500 hover:bg-violet-600",
                    gray: "bg-gray-500 hover:bg-gray-600",
                    sky: "bg-sky-500 hover:bg-sky-600",
                    orange: "bg-orange-500 hover:bg-orange-600",
                  };
                  return (
                    <Button
                      key={f.id}
                      size="sm"
                      className={`text-xs h-7 px-2.5 text-white ${bgMap[f.color] || "bg-gray-500"}`}
                      onClick={() => moveCallsToFolder([...checked], f.id)}
                      data-testid={`move-to-${f.id}`}
                    >
                      → {f.name}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1 mb-3">
          <span className="text-[11px] font-semibold text-gray-500 mr-1">Schnellauswahl:</span>
          {[10, 20, 50, 100, "all"].map((n) => (
            <Button
              key={n}
              size="sm"
              variant="outline"
              className="text-xs px-2 py-1 h-7"
              onClick={() => selectLastN(n)}
              data-testid={`select-last-${n}`}
              title={n === "all" ? "Alle sichtbaren markieren" : `Die letzten ${n} markieren`}
            >
              ✓ {n === "all" ? "Alle" : `Letzte ${n}`}
            </Button>
          ))}
          {checked.size > 0 && (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="text-xs px-2 py-1 h-7 text-gray-600"
                onClick={() => setChecked(new Set())}
                data-testid="clear-selection-btn"
              >
                ✕ Auswahl aufheben
              </Button>
              <Button
                size="sm"
                className="text-xs px-2 py-1 h-7 bg-red-500 hover:bg-red-600 text-white"
                onClick={() => bulkDelete("selected")}
                data-testid="delete-selected-btn"
              >
                🗑 {checked.size} löschen
              </Button>
            </>
          )}
        </div>

        <ScrollArea className="flex-1 max-h-[600px]">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : calls.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-8">Noch keine Anrufe.</div>
          ) : (
            <>
              {total > 0 && (
                <div className="flex items-center justify-between gap-2 mb-2 px-1 py-1.5 bg-slate-50 border border-slate-200 rounded-md">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="text-xs h-7 px-2"
                    data-testid="page-prev-btn"
                  >
                    ← Zurück
                  </Button>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-slate-700">
                      Seite {page} / {totalPages} · {calls.length} von {total}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => load()}
                      className="text-xs h-7 px-2"
                      title="Liste neu laden"
                      data-testid="page-refresh-btn"
                    >
                      🔄
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="text-xs h-7 px-2"
                    data-testid="page-next-btn"
                  >
                    Weiter →
                  </Button>
                </div>
              )}
              <ul className="space-y-1">
              {calls.map((c) => {
                const active = c.call_sid === selectedSid;
                const isChecked = checked.has(c.call_sid);
                const turnCount = (c.turns || []).length;
                return (
                  <li
                    key={c.call_sid}
                    className={`rounded-lg p-2 flex gap-2 cursor-pointer transition-colors ${
                      active ? "bg-emerald-50 border border-emerald-200" : "hover:bg-gray-50"
                    }`}
                    onClick={() => loadDetail(c.call_sid)}
                    data-testid={`call-item-${c.call_sid}`}
                  >
                    <div onClick={(e) => e.stopPropagation()} className="flex items-start pt-0.5">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggle(c.call_sid)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        {c.direction === "outbound" ? (
                          <span className="text-[10px] font-semibold text-orange-600 bg-orange-50 border border-orange-200 rounded px-1.5 py-0" title="Ausgehend">
                            ➡️ RAUS
                          </span>
                        ) : (
                          <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded px-1.5 py-0" title="Eingehend">
                            ⬅️ REIN
                          </span>
                        )}
                        {c.contact_name && (
                          <span className="text-sm font-semibold truncate text-gray-800">
                            {c.contact_name}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-mono truncate text-gray-700">
                        {c.direction === "outbound"
                          ? (c.to_number || "?")
                          : (c.from_number || "?")}
                      </div>
                      <div className="text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
                        <span>{fmtBerlin(c.created_at)}</span>
                        <Badge variant="outline" className="text-[10px] py-0 h-4">
                          {turnCount} Turns
                        </Badge>
                        {c.folder_id && (() => {
                          const folder = folders.find((f) => f.id === c.folder_id);
                          if (!folder) return null;
                          const colorMap = {
                            emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
                            red: "bg-red-50 text-red-700 border-red-200",
                            amber: "bg-amber-50 text-amber-700 border-amber-200",
                            violet: "bg-violet-50 text-violet-700 border-violet-200",
                            gray: "bg-gray-50 text-gray-700 border-gray-200",
                            sky: "bg-sky-50 text-sky-700 border-sky-200",
                            orange: "bg-orange-50 text-orange-700 border-orange-200",
                          };
                          return (
                            <Badge
                              variant="outline"
                              className={`text-[10px] py-0 h-4 ${colorMap[folder.color] || colorMap.gray}`}
                            >
                              📁 {folder.name}
                            </Badge>
                          );
                        })()}
                        {c.source === "elevenlabs" && (
                          <Badge
                            variant="outline"
                            className="text-[10px] py-0 h-4 bg-violet-50 text-violet-700 border-violet-200"
                            data-testid={`elevenlabs-badge-${c.call_sid}`}
                          >
                            11L
                          </Badge>
                        )}
                        {c.status && (
                          <Badge
                            variant="outline"
                            className={`text-[10px] py-0 h-4 ${
                              c.status === "completed" || c.status === "done" ? "text-emerald-600" : "text-gray-500"
                            }`}
                          >
                            {c.status}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            </>
          )}
        </ScrollArea>
      </div>

      {/* RIGHT: detail + analysis */}
      <div className="space-y-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 min-h-[350px]" data-testid="call-detail-panel">
          {!selectedSid ? (
            <div className="flex items-center justify-center h-64 text-sm text-gray-400">
              Wähle links einen Anruf, um das Transkript zu sehen.
            </div>
          ) : !detail ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {detail.direction === "outbound" ? (
                      <span className="text-[10px] font-semibold text-orange-600 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">
                        ➡️ AUSGEHEND
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                        ⬅️ EINGEHEND
                      </span>
                    )}
                    {detail.contact_name && (
                      <span className="text-base font-semibold text-gray-900">
                        {detail.contact_name}
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-gray-900 font-mono">
                    {detail.direction === "outbound"
                      ? (detail.to_number || "?")
                      : (detail.from_number || "?")}
                  </div>
                  <div className="text-xs text-gray-500">
                    {fmtBerlin(detail.created_at)} · CallSid {detail.call_sid?.slice(0, 10)}…
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {folders.length > 0 && (
                    <div className="relative group" data-testid="single-move-folder-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        data-testid="single-move-folder-btn"
                      >
                        📁 In Ordner
                      </Button>
                      <div className="absolute right-0 top-full mt-1 hidden group-hover:block bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[180px] p-1">
                        <button
                          className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100"
                          onClick={async () => {
                            await moveCallsToFolder([detail.call_sid], null);
                            const r = await axios.get(`${API}/admin/phone-calls/${detail.call_sid}`, {
                              headers: { Authorization: `Bearer ${authToken}` },
                            });
                            setDetail(r.data);
                          }}
                          data-testid="single-move-to-none"
                        >
                          📥 Posteingang
                        </button>
                        {folders.map((f) => {
                          const dot = {
                            emerald: "bg-emerald-500", red: "bg-red-500", amber: "bg-amber-500",
                            violet: "bg-violet-500", gray: "bg-gray-500", sky: "bg-sky-500",
                            orange: "bg-orange-500",
                          }[f.color] || "bg-gray-500";
                          const isCurrent = detail.folder_id === f.id;
                          return (
                            <button
                              key={f.id}
                              className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 flex items-center gap-2 ${isCurrent ? "bg-gray-50 font-semibold" : ""}`}
                              onClick={async () => {
                                await moveCallsToFolder([detail.call_sid], f.id);
                                const r = await axios.get(`${API}/admin/phone-calls/${detail.call_sid}`, {
                                  headers: { Authorization: `Bearer ${authToken}` },
                                });
                                setDetail(r.data);
                              }}
                              data-testid={`single-move-to-${f.id}`}
                            >
                              <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />
                              {f.name}
                              {isCurrent && <span className="ml-auto text-emerald-600">✓</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!window.confirm("Diesen Anruf löschen?")) return;
                      await axios.delete(`${API}/admin/phone-calls/${detail.call_sid}`, {
                        headers: { Authorization: `Bearer ${authToken}` },
                      });
                      toast.success("Anruf gelöscht");
                      setSelectedSid(null);
                      setDetail(null);
                      load();
                    }}
                    data-testid="delete-current-call-btn"
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Löschen
                  </Button>
                </div>
              </div>

              <div className="max-h-[600px] overflow-y-auto pr-3" data-testid="transcript-scroll">
                <div className="space-y-3">
                  {(detail.turns || []).length === 0 && (
                    <div className="text-sm text-gray-400 italic">
                      Kein Transkript gespeichert (Anruf war zu kurz oder Bot antwortete nicht).
                    </div>
                  )}
                  {(detail.turns || []).map((t, i) => (
                    <div key={i} className="space-y-1">
                      {t.user && (
                        <div className="flex gap-2">
                          <div className="text-[11px] font-semibold uppercase text-blue-500 w-16 flex-shrink-0">
                            Anrufer
                          </div>
                          <div className="text-sm text-gray-800 flex-1">{t.user}</div>
                        </div>
                      )}
                      {t.assistant && (
                        <div className="flex gap-2">
                          <div className="text-[11px] font-semibold uppercase text-emerald-500 w-16 flex-shrink-0">
                            Bot
                          </div>
                          <div className="text-sm text-gray-700 flex-1">{t.assistant}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Audio recording player - Twilio legacy calls or ElevenLabs calls */}
              {(detail.recording_url || detail.source === "elevenlabs") && (
                <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50 p-3">
                  <div className="text-xs font-semibold uppercase text-purple-700 mb-2 flex items-center gap-2">
                    <Volume2 className="h-3 w-3" />
                    Audioaufnahme {detail.duration_seconds ? `(${detail.duration_seconds}s)` : detail.recording_duration ? `(${detail.recording_duration}s)` : ""}
                    {detail.source === "elevenlabs" && (
                      <span className="ml-2 text-[10px] normal-case font-normal text-violet-600">ElevenLabs</span>
                    )}
                  </div>
                  <audio
                    controls
                    src={
                      detail.source === "elevenlabs"
                        ? `${API}/admin/elevenlabs/audio/${detail.elevenlabs_conversation_id || detail.call_sid}?token=${authToken}`
                        : `${API}/admin/phone-calls/${detail.call_sid}/audio?token=${authToken}`
                    }
                    className="w-full"
                    data-testid="call-audio-player"
                  >
                    Dein Browser unterstützt keine Audio-Wiedergabe.
                  </audio>
                </div>
              )}

              {/* Single-call GPT-4o analysis */}
              <SingleCallAnalysis
                analysis={detail.analysis}
                onAnalyze={analyzeSingle}
                loading={analyzingSingle}
              />
            </div>
          )}
        </div>

        {/* Analysis */}
        <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-purple-50 to-white p-4" data-testid="analysis-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              <h3 className="font-semibold text-gray-900">GPT-4o Analyse</h3>
              <span className="text-xs text-gray-500">
                {checked.size > 0 ? `${checked.size} ausgewählt` : "→ letzte 10"}
              </span>
            </div>
            <Button
              onClick={analyze}
              disabled={analyzing || calls.length === 0}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="analyze-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Analysiere…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-1" /> Analysieren
                </>
              )}
            </Button>
          </div>
          {analysis ? (
            <ScrollArea className="max-h-[400px] pr-3">
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm text-gray-800">
                {analysis}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-sm text-gray-400 italic">
              Wähle Anrufe aus und klicke &bdquo;Analysieren&ldquo; &mdash; GPT-4o erkennt Intents, Einwände, Qualifikation und gibt Empfehlungen.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================ UI Bits */

function Card({ title, icon, children, testId }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h2 className="font-semibold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Kv({ label, value, pill }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-gray-500 tracking-wide">{label}</div>
      {pill ? (
        <Badge className={pill === "green" ? "bg-emerald-100 text-emerald-700 mt-1" : "bg-red-100 text-red-700 mt-1"}>
          {value}
        </Badge>
      ) : (
        <div className="text-sm font-medium text-gray-900 truncate">{value ?? "—"}</div>
      )}
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2 block">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function NumberField({ label, emoji, number, enabled, onNumber, onEnabled, testId }) {
  return (
    <div className={`rounded-lg border p-4 ${enabled ? "border-emerald-200 bg-emerald-50/40" : "border-gray-200 bg-gray-50/40"}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-gray-800">
          {emoji} {label}
        </div>
        <Switch checked={enabled} onCheckedChange={onEnabled} data-testid={`${testId}-toggle`} />
      </div>
      <Input
        value={number || ""}
        onChange={(e) => onNumber(e.target.value)}
        placeholder="+49 …"
        className="font-mono"
        data-testid={`${testId}-input`}
      />
    </div>
  );
}

const EMOTION_STYLES = {
  interessiert: "bg-emerald-100 text-emerald-700",
  begeistert: "bg-emerald-100 text-emerald-700",
  skeptisch: "bg-amber-100 text-amber-700",
  ungeduldig: "bg-orange-100 text-orange-700",
  frustriert: "bg-red-100 text-red-700",
  neutral: "bg-gray-100 text-gray-700",
};

const QUAL_STYLES = {
  sehr_qualifiziert: "bg-emerald-100 text-emerald-700",
  qualifiziert: "bg-lime-100 text-lime-700",
  unklar: "bg-gray-100 text-gray-700",
  nicht_qualifiziert: "bg-red-100 text-red-700",
};

function SingleCallAnalysis({ analysis, onAnalyze, loading }) {
  if (!analysis) {
    return (
      <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-purple-800">
            <Sparkles className="h-4 w-4" />
            Noch keine KI-Analyse für diesen Anruf.
          </div>
          <Button
            onClick={onAnalyze}
            disabled={loading}
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="analyze-single-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Analysiere…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-1" /> KI-Analyse starten
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  const a = analysis;
  const lead = a.lead_data || {};
  return (
    <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50/60 p-4 space-y-3" data-testid="single-call-analysis">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-purple-900">
          <Sparkles className="h-4 w-4" /> KI-Analyse
        </div>
        <Button
          onClick={onAnalyze}
          disabled={loading}
          size="sm"
          variant="outline"
          className="text-xs h-7"
          data-testid="reanalyze-single-btn"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCcw className="h-3 w-3 mr-1" />}
          Neu analysieren
        </Button>
      </div>

      {a.summary && (
        <div>
          <div className="text-[10px] uppercase font-semibold text-purple-600 mb-1">Zusammenfassung</div>
          <div className="text-sm text-gray-800">{a.summary}</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {a.caller_intent && (
          <Badge className="bg-blue-100 text-blue-700">📞 {a.caller_intent}</Badge>
        )}
        {a.emotion && (
          <Badge className={EMOTION_STYLES[a.emotion] || "bg-gray-100 text-gray-700"}>
            😐 {a.emotion}
          </Badge>
        )}
        {a.qualification && (
          <Badge className={QUAL_STYLES[a.qualification] || "bg-gray-100 text-gray-700"}>
            ⭐ {a.qualification.replace(/_/g, " ")}
          </Badge>
        )}
        {a.bot_performance && (
          <Badge className="bg-indigo-100 text-indigo-700">
            🤖 Bot: {a.bot_performance.replace(/_/g, " ")}
          </Badge>
        )}
      </div>

      {(lead.name || lead.phone || lead.city || lead.age) && (
        <div className="rounded-lg bg-white border border-purple-100 p-3">
          <div className="text-[10px] uppercase font-semibold text-purple-600 mb-2">Erkannte Kontaktdaten</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {lead.name && <div><span className="text-gray-500">Name:</span> <strong>{lead.name}</strong></div>}
            {lead.phone && <div><span className="text-gray-500">Handy:</span> <strong className="font-mono">{lead.phone}</strong></div>}
            {lead.city && <div><span className="text-gray-500">Stadt:</span> {lead.city}</div>}
            {lead.age && <div><span className="text-gray-500">Alter:</span> {lead.age}</div>}
          </div>
        </div>
      )}

      {a.key_points && a.key_points.length > 0 && (
        <div>
          <div className="text-[10px] uppercase font-semibold text-purple-600 mb-1">Kernaussagen des Anrufers</div>
          <ul className="text-sm text-gray-800 space-y-0.5 list-disc pl-5">
            {a.key_points.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}

      {a.qualification_reason && (
        <div className="text-xs text-gray-600 italic">
          <strong>Warum:</strong> {a.qualification_reason}
        </div>
      )}

      {a.bot_issues && a.bot_issues.length > 0 && (
        <div>
          <div className="text-[10px] uppercase font-semibold text-red-600 mb-1">Was der Bot besser machen könnte</div>
          <ul className="text-sm text-red-800 space-y-0.5 list-disc pl-5">
            {a.bot_issues.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}

      {a.recommended_next_step && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
          <div className="text-[10px] uppercase font-semibold text-emerald-700 mb-1">Nächster Schritt</div>
          <div className="text-sm text-emerald-900">{a.recommended_next_step}</div>
        </div>
      )}
    </div>
  );
}
